import { useParams } from '../../../contexts/RouterContext';
import { compactRevision } from '../../../helpers/revisions';
import { useReleaseGroup } from '../../../hooks/api/releaseGroups/useReleaseGroup';
import { useReleaseGroupRelease } from '../../../hooks/api/releaseGroups/useReleaseGroupRelease';
import useRevision from '../../../hooks/api/revisions/useRevision';
import { useSelectedReleaseIdForRoute } from '../../../redux/project_grouping/hooks';
import { toFiniteNumber } from '../../../utils';
import { isReleaseGroupScope } from '../types';
import type { ReportsV3Params, ReportV3Format } from '../types';

import { getFormatLabel } from './useFormatSelector';

/**
 * Names a report for notifications, e.g. "PDF report for Acme Platform 2.3" or
 * "SPDX report for my-service (main@a1b2c3d)". Names come from data the report page
 * has already loaded, and are left out while it is still loading.
 *
 * @param format The report format.
 */
export function useReportDisplayName(format: ReportV3Format): string {
  const params = useParams<ReportsV3Params>();
  const isReleaseGroup = isReleaseGroupScope(params);
  const selectedReleaseId = useSelectedReleaseIdForRoute();
  const releaseGroupId = isReleaseGroup ? toFiniteNumber(params.projectGroupId) ?? 0 : 0;
  const releaseId = isReleaseGroup ? toFiniteNumber(params.releaseId) ?? selectedReleaseId : null;
  // Same query keys as the release group page's own fetches, so these read its cache.
  const { data: releaseGroup } = useReleaseGroup(releaseGroupId, isReleaseGroup);
  const { data: release } = useReleaseGroupRelease(releaseGroupId, releaseId);
  // Same query key as the report page's own revision fetch, so this reads its cache.
  const revisionLocator = isReleaseGroup ? '' : `${params.project_id}$${params.revision_id}`;
  const revision = useRevision(revisionLocator, !isReleaseGroup);

  const reportName = `${getFormatLabel(params.reportType, format)} report`;

  let subject: string | null = null;
  if (isReleaseGroup) {
    subject = releaseGroup && release ? `${releaseGroup.title} ${release.title}` : null;
  } else if (revision.data?.project) {
    const shortRevision = compactRevision(revisionLocator);
    const revisionName = params.reference && params.reference !== shortRevision
      ? `${params.reference}@${shortRevision}`
      : shortRevision;
    subject = `${revision.data.project.title} (${revisionName})`;
  }

  return subject ? `${reportName} for ${subject}` : reportName;
}
